import { Partner as PartnerRaw } from '@provision/entity-types';
import {
  DefaultSla,
  formatMembershipName,
  isCppActive,
  PRE_LITS_PLACEHOLDER_PLATFORM_SLA_ID,
  ServiceLevel,
} from '@provision/service-level-utils';
import dayjs from 'dayjs';

/**
 * Formats a Service Level display name, prefixing "CPP" while a complimentary
 * period is active (cppExpDate in the future). Empty input surfaces as Non-Member.
 */
export function formatServiceLevelWithCpp(
  displayName?: string | null,
  cppExpDate?: string | null,
): string {
  if (!displayName) {
    return ServiceLevel.NonMember;
  }

  return formatMembershipName(displayName, isCppActive(cppExpDate));
}

// https://www.notion.so/onevision/Account-for-LiTS-in-the-side-panel-ProVision-App-fbb591ec2a094786a53b527706a3486a
export function getServiceLevelValue({
  partner,
  platformSlaId,
  timeZone,
}: {
  partner?: PartnerRaw;
  platformSlaId?: string | null;
  timeZone: string;
}): string | null | undefined {
  if (!partner) {
    return undefined;
  }

  // Secure is its own tier and is unaffected by LitS gating.
  if (platformSlaId === DefaultSla.SecurityOnly) {
    return DefaultSla.SecurityOnly;
  }

  // Without a LitS date there is no pre-launch gating; surface the SLA as stored
  // (`null` represents Non-Member).
  if (!partner.litsDate) {
    return platformSlaId ?? null;
  }

  const litsDate = dayjs.tz(partner.litsDate, timeZone);
  const nowInTimezone = dayjs().tz(timeZone);

  const isActivated =
    litsDate.isBefore(nowInTimezone, 'day') || litsDate.isSame(nowInTimezone, 'day');

  // After LitS, or whenever a real SLA is already set, use the stored platform SLA.
  if (isActivated || platformSlaId) {
    return platformSlaId ?? null;
  }

  // Before LitS activation with no active SLA: surface the Pre-LitS placeholder.
  return PRE_LITS_PLACEHOLDER_PLATFORM_SLA_ID;
}
